"use client";

import { useId, useState, useTransition } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { CircleHelp, PencilLine, Send, Truck, X } from "lucide-react";

import { cancelPoBeforeSendAction } from "@/app/supply/po/[orderId]/edit/actions";

import { describeTransportError } from "../_lib/client-errors";
import { pick, type SupplyLang } from "../_lib/lang";
import {
  startedDeliveriesLabel,
  UNSENT_PO_COPY as COPY,
  UNSENT_PO_VISIBLE_ROWS,
  unsentPoShowAllLabel,
  type UnsentPoCardModel,
  type UnsentPoRow,
} from "../_lib/po-outstanding";
import { COPY as APPROVALS_COPY } from "../approvals/copy";
import { Numeral, StatusChip, StatusRow } from "./elevated";
import { Toast } from "./elevated/desk-client";
import { WhatsAppHandoff } from "./PoSendRail";

/**
 * "POs not sent yet" — every approved, unsent supplier PO of the outlet, on
 * the Supply home and order pages (Shaq, 2026-09-17), behind the same switch
 * as "Edit before sending".
 *
 * Small on purpose: the page's own work stays the focus. The three OLDEST
 * rows show; "Show all" reveals the rest (flat keyed siblings, hidden — never
 * a per-group wrapper). A row that looks forgotten asks "Still needed?" and
 * offers the two ways out: record the send (the existing WhatsApp share → "I
 * sent it", with its content key) or cancel it with a reason. Deliveries that
 * already started are one "finish receiving" line, never "to send".
 *
 * Colour anchor: the card's info wash and the count in the full hue; the
 * nudge is an amber chip WITH an icon and words (never colour alone).
 */
export function UnsentPoCard({ model, lang }: { model: UnsentPoCardModel; lang: SupplyLang }) {
  const [expanded, setExpanded] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  // The order page renders the phone and desk layouts at once: ids must be per instance.
  const headingId = useId();

  if (model.state === "failed") {
    return (
      <section aria-labelledby={headingId} className="supply-unsent-po-wrap" data-testid="unsent-po-card">
        <div className="supply-unsent-po supply-tone-info">
          <h2 id={headingId} className="supply-unsent-po-title">{pick(lang, COPY.heading)}</h2>
          <StatusRow tone="danger">{pick(lang, COPY.loadFailed)}</StatusRow>
        </div>
      </section>
    );
  }

  const { rows, started } = model;
  if (rows.length === 0 && started === null) return null;
  const hiddenCount = Math.max(0, rows.length - UNSENT_PO_VISIBLE_ROWS);

  return (
    <section aria-labelledby={headingId} className="supply-unsent-po-wrap" data-testid="unsent-po-card">
      <div className="supply-unsent-po supply-tone-info">
        <div className="supply-unsent-po-head">
          <h2 id={headingId} className="supply-unsent-po-title">{pick(lang, COPY.heading)}</h2>
          {rows.length > 0 ? <Numeral value={rows.length} size={20} tone="info" /> : null}
        </div>

        {started ? (
          <StatusRow tone="accent" href={started.href}>
            <span className="inline-flex items-center gap-1.5">
              <Truck className="size-4 shrink-0" aria-hidden />
              {startedDeliveriesLabel(started.count, lang)} · {pick(lang, COPY.finishReceiving)}
            </span>
          </StatusRow>
        ) : null}

        {rows.length > 0 ? (
          <ul className="supply-unsent-po-list">
            {rows.map((row, index) => (
              <li key={row.orderId} hidden={!expanded && index >= UNSENT_PO_VISIBLE_ROWS} data-po={row.orderId}>
                <UnsentPoItem row={row} lang={lang} onResult={setToast} />
              </li>
            ))}
          </ul>
        ) : null}

        {hiddenCount > 0 ? (
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded((open) => !open)}
            className="supply-unsent-po-more"
          >
            {expanded ? pick(lang, COPY.showFewer) : unsentPoShowAllLabel(rows.length, lang)}
          </button>
        ) : null}
      </div>
      {toast ? <Toast onDismiss={() => setToast(null)}>{toast}</Toast> : null}
    </section>
  );
}

type Panel = "none" | "send" | "cancel";

function UnsentPoItem({
  row,
  lang,
  onResult,
}: {
  row: UnsentPoRow;
  lang: SupplyLang;
  onResult: (message: string) => void;
}) {
  const [panel, setPanel] = useState<Panel>("none");
  const toggle = (next: Panel) => setPanel((current) => (current === next ? "none" : next));

  return (
    <article className="supply-unsent-po-row" data-stale={row.stale ? "true" : "false"}>
      <span className="supply-unsent-po-doc">{row.docCode}</span>
      <span className={`supply-unsent-po-total ${row.totalLabel ? "" : "supply-unsent-po-total-missing"}`}>
        {row.totalLabel ?? pick(lang, COPY.priceNeeded)}
      </span>
      <span className="supply-unsent-po-wide supply-unsent-po-supplier">{row.supplierName}</span>
      <span className="supply-unsent-po-wide supply-unsent-po-meta">
        {row.ageLabel ? `${row.raisedByLabel} · ${row.ageLabel}` : row.raisedByLabel}
      </span>

      {row.stale ? (
        <div className="supply-unsent-po-wide supply-unsent-po-nudge">
          <StatusChip tone="accent">
            <CircleHelp className="size-3.5" aria-hidden />
            {pick(lang, COPY.stillNeeded)}
          </StatusChip>
          <p>{pick(lang, COPY.staleHint)}</p>
        </div>
      ) : null}

      <div className="supply-unsent-po-wide supply-unsent-po-actions">
        {row.whatsapp && row.canRecordSend ? (
          <button
            type="button"
            aria-expanded={panel === "send"}
            onClick={() => toggle("send")}
            className="supply-unsent-po-btn supply-unsent-po-btn-send"
          >
            <Send className="size-4" aria-hidden />
            {panel === "send" ? pick(lang, COPY.hideSend) : pick(lang, row.stale ? COPY.alreadySent : COPY.send)}
          </button>
        ) : null}
        {row.editHref ? (
          <Link href={row.editHref} prefetch={false} className="supply-unsent-po-btn">
            <PencilLine className="size-4" aria-hidden />
            {pick(lang, APPROVALS_COPY.editBeforeSendingLink)}
          </Link>
        ) : null}
        {row.stale && row.canCancel ? (
          <button
            type="button"
            aria-expanded={panel === "cancel"}
            onClick={() => toggle("cancel")}
            className="supply-unsent-po-btn supply-unsent-po-btn-danger"
          >
            <X className="size-4" aria-hidden />
            {pick(lang, COPY.cancel)}
          </button>
        ) : null}
      </div>
      {row.whatsapp ? (
        // The WhatsApp actions admit only the raiser or an approver — say who.
        row.canRecordSend ? null : <p className="supply-unsent-po-wide supply-unsent-po-hint">{row.recordAskLabel}</p>
      ) : (
        <p className="supply-unsent-po-wide supply-unsent-po-hint">
          {/* No channel at all: nobody can send it until HQ adds one — never "HQ sends this". */}
          {pick(lang, row.noChannel ? APPROVALS_COPY.hqOwnershipHint : COPY.hqSends)}
        </p>
      )}

      {panel === "send" && row.canRecordSend ? (
        <div className="supply-unsent-po-wide">
          <WhatsAppHandoff row={row} lang={lang} onResult={onResult} />
        </div>
      ) : null}
      {panel === "cancel" ? (
        <div className="supply-unsent-po-wide">
          <CancelForm row={row} lang={lang} onResult={onResult} onClose={() => setPanel("none")} />
        </div>
      ) : null}
    </article>
  );
}

function CancelForm({
  row,
  lang,
  onResult,
  onClose,
}: {
  row: UnsentPoRow;
  lang: SupplyLang;
  onResult: (message: string) => void;
  onClose: () => void;
}) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [reason, setReason] = useState("");
  const inputId = useId();

  function submit() {
    const trimmed = reason.trim();
    if (!trimmed) return;
    startTransition(async () => {
      try {
        const result = await cancelPoBeforeSendAction({ orderId: row.orderId, reason: trimmed });
        // The reason stays typed on a refusal — nothing is lost.
        onResult(result.ok ? result.message : result.error);
        if (result.ok || result.cancelled) onClose();
        router.refresh();
      } catch (error) {
        const friendly = describeTransportError(error);
        if (friendly === null) throw error;
        onResult(friendly);
      }
    });
  }

  return (
    <div className="supply-unsent-po-cancel">
      <label htmlFor={inputId} className="text-[12px] font-bold text-[var(--danger)]">
        {pick(lang, COPY.cancelReasonLabel)} — {row.docCode}
      </label>
      <textarea
        id={inputId}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        rows={2}
        maxLength={300}
        placeholder={pick(lang, COPY.cancelReasonPlaceholder)}
        // 16px floor: a focused field under 16px makes iOS zoom and stay zoomed.
        className="w-full resize-none rounded-[12px] border border-[var(--border)] bg-[var(--raised)] p-3 text-[16px] font-medium text-[var(--text)] placeholder:text-[var(--muted)]"
      />
      <div className="supply-unsent-po-actions">
        <button type="button" onClick={onClose} disabled={isPending} className="supply-unsent-po-btn">
          {pick(lang, COPY.cancelKeep)}
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={isPending || reason.trim().length === 0}
          className="supply-unsent-po-btn supply-unsent-po-btn-danger"
        >
          {isPending ? pick(lang, COPY.cancelling) : pick(lang, COPY.cancelConfirm)}
        </button>
      </div>
    </div>
  );
}
